import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import newBadge from "assets/lottie/newBadge.json";
import {
  BellIcon,
  Button,
  CalendarIcon,
  CircleQuestionMarkIcon,
  ClockFilledIcon,
  DropdownMenu,
  InfoChip,
  InfoIcon,
  TableChip,
  Tooltip,
  Vertical3DotsIcon,
} from "components";
import type { InfoChipTone } from "components/chip/InfoChip";
import {
  useGetIntegrationNotificationSettingsById,
  useGlobalStore,
  useUpsertIntegrationNotificationSettings,
} from "controller";
import Lottie from "lottie-react";
import { type Ref, useState } from "react";
import toast from "react-hot-toast";
import { addMixpanelEvent } from "utilz/helpers";
import { isoToDateAndTime } from "utilz/helpers/formatDate";

type IntegrationCardProps = {
  logo: string;
  label: string;
  description: string;
  isActive: boolean;
  isConnected: boolean;
  menuItems: boolean;
  hideSyncToggle?: boolean;
  onClick?: ({ value, source }: { value: boolean; source: string }) => void;
  value: string;
  isAvailable: boolean;
  sourceId?: string;
  last_synced_at?: string;
  /** Renders the Last synced line as a failure instead of a date. */
  syncFailed?: boolean;
  status?: "Connected" | "Disconnected" | "Pending" | string;
  /**
   * Gates the integration behind a beta-access request: the card renders
   * "Request access" where "Connect" would sit. Set by the caller from the
   * feature flag, so the card itself stays flag-agnostic.
   */
  requiresAccessRequest?: boolean;
  onRequestAccessClick?: (source: string) => void;
  onConnectClick?: ({
    source,
    isConnected,
  }: {
    source: string;
    isConnected: boolean;
  }) => void;
  menuOptions?: {
    name?: string;
    value?: string;
    type?: string;
    icon?: any;
    tooltip?: string;
  }[];
  onMenuItemClick: ({
    name,
    value,
    type,
  }: {
    name: string;
    value: string;
    type: string;
  }) => void;
  isLoadingCard?: boolean;
  documentation?: string;
  /** Gradient "beta" tag beside the label. Independent of connection state. */
  isBeta?: boolean;
  /** Lottie NEW badge, top-right. Only while disconnected. */
  isNew?: boolean;
  /** The card's root element, for the screen to scroll a card into view. */
  cardRef?: Ref<HTMLDivElement>;
};

/**
 * `value`s from the "Remote Monitoring & Management" category in
 * `integrationList.tsx`. Only the four that are currently connectable — the
 * others in that category are not yet available, so they could not reach the
 * menu anyway, and adding one here is part of making it available.
 */
const RMM_SOURCE_VALUES = ["ncentral", "ninjaOne", "datto", "level"];

const getStatusBadgeProps = (
  isConnected: boolean,
  status?: string,
): { tone: InfoChipTone; label: string } => {
  if (!isConnected) {
    return { tone: "error", label: "Disconnected" };
  }
  if (status === "Pending") {
    return { tone: "warning", label: "Pending" };
  }
  if (status === "Disconnected") {
    return { tone: "error", label: "Disconnected" };
  }
  return { tone: "success", label: "Connected" };
};

export const IntegrationCard = ({
  logo,
  label,
  description,
  isActive,
  isConnected,
  onClick,
  value,
  isAvailable,
  onConnectClick,
  requiresAccessRequest,
  onRequestAccessClick,
  sourceId,
  last_synced_at,
  syncFailed,
  status,
  menuItems,
  hideSyncToggle,
  menuOptions,
  onMenuItemClick,
  isLoadingCard,
  documentation,
  isBeta,
  isNew,
  cardRef,
}: IntegrationCardProps) => {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);
  const [isShowDocumentationLink, setIsShowDocumentationLink] = useState(false);

  const statusBadge = getStatusBadgeProps(isConnected, status);
  const formattedLastSyncedAt = last_synced_at
    ? isoToDateAndTime({
        isodate: last_synced_at,
        timezone,
        // Matches the design: "12:00AM, 21 Dec 2026"
        format: "hh:mmA, DD MMM YYYY",
      })
    : "N/A";

  // Sync enable/disable + notifications now live in the 3-dot menu (no on-card
  // toggle/bell). Notification state is read here so the menu label reflects it.
  const { data: notificationSettings } =
    useGetIntegrationNotificationSettingsById(
      isConnected ? sourceId : undefined,
    );
  const { mutateAsync: upsertIntegrationNotificationSettings } =
    useUpsertIntegrationNotificationSettings();

  const handleNotificationToggle = async () => {
    const current = notificationSettings?.is_enabled ?? true;
    try {
      await upsertIntegrationNotificationSettings({
        source_id: sourceId as string,
        is_enabled: !current,
      });
    } catch {
      toast.error("Failed to update notification settings");
    }
  };

  // Per design: only RMM integrations expose the auto-ticket sync + notification
  // toggles in the 3-dot menu. Everything else shows Disconnect + Configure.
  // Menu order: Disconnect → (Disable/Enable auto-creating tickets) → Configure
  // → (Turn on/off notification).
  //
  // Named explicitly rather than excluding the ones that are not. This began as
  // `value !== "qb" && value !== "xero"`, which made every integration added
  // afterwards an RMM by default — Stripe, a payment processor with no alerts to
  // raise tickets from and no row in the notification-settings table, was
  // offering both toggles. An allowlist fails the safe way round: a new RMM
  // missing its toggles is visible and gets reported, where a payment
  // integration quietly growing them is not.
  const isRmmIntegration =
    isConnected && !hideSyncToggle && RMM_SOURCE_VALUES.includes(value);
  const notificationsEnabled = notificationSettings?.is_enabled !== false;

  const disconnectOption = (menuOptions ?? []).find(
    (option) => option.value === "deactivate",
  );
  const configureOption = (menuOptions ?? []).find(
    (option) => option.value === "configure",
  );
  const reconnectOption = (menuOptions ?? []).find(
    (option) => option.value === "reconnect",
  );
  const syncNowOption = (menuOptions ?? []).find(
    (option) => option.value === "sync_now",
  );

  const resolvedMenuOptions = [
    ...(disconnectOption ? [disconnectOption] : []),
    ...(reconnectOption ? [reconnectOption] : []),
    ...(isRmmIntegration
      ? [
          {
            name: isActive
              ? "Disable auto-creating tickets"
              : "Enable auto-creating tickets",
            value: isActive ? "sync-deactivate" : "sync-activate",
            type: value,
          },
        ]
      : []),
    ...(configureOption ? [configureOption] : []),
    ...(syncNowOption ? [syncNowOption] : []),
    ...(isRmmIntegration
      ? [
          {
            name: notificationsEnabled
              ? "Turn off notification"
              : "Turn on notification",
            value: "notifications-toggle",
            type: value,
          },
        ]
      : []),
  ];

  return (
    <Box
      ref={cardRef}
      onMouseEnter={() => setIsShowDocumentationLink(true)}
      onMouseLeave={() => setIsShowDocumentationLink(false)}
      sx={{
        position: "relative",
      }}
    >
      <Stack
        sx={{
          boxShadow:
            "0px 0px 4px 0px rgba(0, 0, 0, 0.12), 0px 0px 2px 0px rgba(0, 0, 0, 0.04)",
          padding: "24px",
          borderRadius: "8px",
          height: "280px",
          width: "292px",
          gap: "8px",
          position: "relative",
          justifyContent: "space-between",
        }}
      >
        <Stack gap="8px">
          {/* Header Row: Logo, Status Badge, and Options Menu */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Box
              component="img"
              src={logo}
              alt={label}
              sx={{
                width: "auto",
                height: "40px",
                objectFit: "contain",
              }}
            />

            <Stack direction="row" alignItems="center" gap="4px">
              {isConnected && menuItems && (
                <DropdownMenu
                  paperSx={{ minWidth: "182px" }}
                  menuListItemSx={{ width: "182px" }}
                  onOptionClick={({
                    name = "",
                    value: optionValue = "",
                    type = "",
                  }) => {
                    if (optionValue === "sync-activate") {
                      onClick?.({ value: true, source: value });
                      return;
                    }
                    if (optionValue === "sync-deactivate") {
                      onClick?.({ value: false, source: value });
                      return;
                    }
                    if (optionValue === "notifications-toggle") {
                      handleNotificationToggle();
                      return;
                    }
                    onMenuItemClick({ name, value: optionValue, type });
                  }}
                  options={resolvedMenuOptions}
                >
                  <IconButton
                    aria-label="Open integration options menu"
                    disableFocusRipple
                    sx={{ borderRadius: "4px", p: "2px" }}
                  >
                    <Vertical3DotsIcon />
                  </IconButton>
                </DropdownMenu>
              )}
            </Stack>
          </Stack>

          <Typography
            variant="caption-2"
            sx={{
              position: "relative",
              alignSelf: "flex-start",
              ...(isBeta && {
                "&::after": {
                  content: "'beta'",
                  position: "absolute",
                  right: 0,
                  top: 0,
                  transform: "translate(110%, -40%)",
                  fontSize: "10px",
                  fontWeight: "bold",
                  background:
                    "linear-gradient(279.75deg, #ED25C1 -8.98%, #DA64ED 47.33%, #A74FFF 66.54%, #874FFF 82.24%, #4D25ED 98.62%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  whiteSpace: "nowrap",
                  zIndex: 1,
                },
              }),
            }}
          >
            {label}
          </Typography>

          {isNew && !isConnected && (
            <Lottie
              animationData={newBadge}
              loop={true}
              style={{
                position: "absolute",
                top: "8px",
                right: "8px",
                height: "32px",
              }}
            />
          )}

          <Typography variant="body-small">{description}</Typography>
          {/* Only offered where there is something to open. The link used to
              render for every available integration, so one without a
              documentation URL showed a "How To" that opened nothing. */}
          {isAvailable && documentation && (
            <Stack
              direction="row"
              alignItems="center"
              gap="4px"
              sx={{
                cursor: "pointer",
                visibility: isShowDocumentationLink ? "visible" : "hidden",
              }}
              onClick={() => {
                if (documentation) {
                  window.open(documentation, "_blank", "noopener,noreferrer");
                }
              }}
            >
              <CircleQuestionMarkIcon
                color="primary"
                sx={{
                  width: "16px",
                  height: "16px",
                  mt: "2px",
                }}
              />
              <Typography
                variant="link-small"
                sx={{
                  fontWeight: 600,
                  fontSize: "12px",
                  lineHeight: "16px",
                  letterSpacing: "0px",
                }}
              >
                How To
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* Footer Row */}
        <Stack direction="row">
          {isAvailable ? (
            <ButtonSection
              sourceId={sourceId}
              isConnected={isConnected}
              isActive={isActive}
              source={value}
              onClick={onClick}
              onConnectClick={onConnectClick}
              requiresAccessRequest={requiresAccessRequest}
              onRequestAccessClick={onRequestAccessClick}
              value={value}
              statusBadge={statusBadge}
              hideSyncToggle={hideSyncToggle}
              last_synced_at={last_synced_at}
              syncFailed={syncFailed}
              formattedLastSyncedAt={formattedLastSyncedAt}
              onMenuItemClick={({ name, value, type }) => {
                if (value === "configure") {
                  onConnectClick?.({ source: type, isConnected });
                } else {
                  onMenuItemClick({ name, value, type });
                }
              }}
              menuOptions={menuOptions}
              menuItems={menuItems}
            />
          ) : (
            <Stack
              direction="row"
              sx={{
                width: "100%",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <TableChip
                containerStyle={{
                  backgroundColor: theme.palette.warning.tint,
                  width: "120px",
                }}
                content="Coming Soon"
                type="warning"
                startIcon={
                  <ClockFilledIcon
                    htmlColor={theme.palette.warning.main}
                    fontSize="small"
                  />
                }
              />
              <Tooltip title="Notify Me">
                <Box>
                  <Button
                    variant="secondary"
                    isIconOnly
                    aria-label="Notify me when available"
                    icon={<BellIcon htmlColor={theme.palette.lightBg.high} />}
                    onClick={() => {
                      addMixpanelEvent({
                        event: `${value}_notify_me` as Parameters<
                          typeof addMixpanelEvent
                        >[0]["event"],
                      });
                      toast.success(`You’ll be notified when ${label} is live`);
                    }}
                  />
                </Box>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </Stack>

      {isLoadingCard && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <CircularProgress size={30} />
        </Box>
      )}
    </Box>
  );
};

const ButtonSection = ({
  isConnected,
  isActive,
  source,
  sourceId,
  onClick,
  onConnectClick,
  requiresAccessRequest,
  onRequestAccessClick,
  value,
  statusBadge,
  hideSyncToggle,
  last_synced_at,
  syncFailed,
  formattedLastSyncedAt,
  onToggle,
}: {
  isConnected: boolean;
  isActive: boolean;
  source: string;
  sourceId?: string;
  onClick?: ({ value, source }: { value: boolean; source: string }) => void;
  onConnectClick?: ({
    source,
    isConnected,
  }: {
    source: string;
    isConnected: boolean;
  }) => void;
  requiresAccessRequest?: boolean;
  onRequestAccessClick?: (source: string) => void;
  value: string;
  statusBadge: { tone: InfoChipTone; label: string };
  hideSyncToggle?: boolean;
  last_synced_at?: string;
  syncFailed?: boolean;
  formattedLastSyncedAt?: string;
  menuOptions?: {
    name?: string;
    value?: string;
    type?: string;
    icon?: any;
    tooltip?: string;
  }[];
  menuItems?: boolean;
  onMenuItemClick: ({
    name,
    value,
    type,
  }: {
    name: string;
    value: string;
    type: string;
  }) => void;
  onToggle?: () => void;
}) => {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ width: "100%" }}
    >
      {!isConnected && (
        <Button
          variant="secondary"
          sx={{
            alignSelf: "flex-start",
          }}
          onClick={() => {
            if (requiresAccessRequest) {
              onRequestAccessClick?.(source);
              return;
            }
            onConnectClick?.({ source, isConnected });
          }}
          isResponsive={false}
          size="small"
        >
          {requiresAccessRequest ? "Request access" : "Connect"}
        </Button>
      )}

      {isConnected && (
        <Stack sx={{ width: "100%", gap: "12px" }}>
          {/* Status pill (relocated from the card header) + the sync toggle/bell.
              Pax8 is connect-only, so per design it shows the status pill + Last
              synced alone (its actions live in the 3-dot menu); every other
              integration keeps its toggle/bell here unchanged. The divider only
              renders when a Last synced line follows it. */}
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              width: "100%",
              ...((last_synced_at || syncFailed) && {
                pb: "12px",
                borderBottom: `1px solid ${theme.palette.neutral["015"]}`,
              }),
            }}
          >
            <InfoChip
              label={statusBadge.label}
              tone={statusBadge.tone}
              icon="dot"
            />
          </Stack>

          {(last_synced_at || syncFailed) && (
            <Stack direction="row" alignItems="center" gap="6px">
              <CalendarIcon
                sx={{ color: theme.palette.lightBg.low, fontSize: "16px" }}
              />
              <Typography
                variant="body-xsmall"
                sx={{ color: theme.palette.lightBg.low }}
              >
                Last synced:
              </Typography>
              {syncFailed ? (
                <>
                  <Typography
                    variant="body-small"
                    sx={{ color: theme.palette.error.main }}
                  >
                    Failed
                  </Typography>
                  {/* The badge says a sync failed; only this says why, and
                      what to do about it. `disableSentenceCase` because the
                      title is written prose — the default transform would
                      lowercase "Stripe", which is absent from
                      PRESERVE_CASE_CONSTANTS unlike every other integration. */}
                  <Tooltip
                    title="This Stripe account cannot accept payments yet. Complete your account setup in Stripe."
                    disableSentenceCase
                  >
                    <InfoIcon
                      sx={{
                        color: theme.palette.error.main,
                        fontSize: "16px",
                        cursor: "pointer",
                      }}
                    />
                  </Tooltip>
                </>
              ) : (
                <Typography
                  variant="body-xsmall"
                  sx={{ color: theme.palette.lightBg.low }}
                >
                  {formattedLastSyncedAt}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};
